<script lang="ts">
  import ProgressSteps, {
    type ProgressStep,
    type ProgressStepContext,
  } from "@kontextwerk/web-sdk/ui/navigation/ProgressSteps.svelte"
  import { newApproval } from "@kontextwerk/web-sdk/core/overlay/approvals"
  import { newNotification } from "@kontextwerk/web-sdk/core/overlay/notifications"
  import {
    mdiAccountMultiplePlus,
    mdiCalendarClockOutline,
    mdiCalendarSync,
    mdiCogOutline,
    mdiFolderUpload,
    mdiUpload,
  } from "@mdi/js"
  import { spaNavigate } from "@kontextwerk/control-web-frame/navigation"
  import { onDestroy, onMount, untrack } from "svelte"
  import type { Snippet } from "svelte"
  import {
    updateOrganizationOnboardingStep,
    type OrganizationOnboardingResponse,
    type OrganizationOnboardingStep,
  } from "../../../../../../../../../../../../api/kehrblatt/organization-onboarding"
  import { LOCKED_WORKFLOW_STEP_MESSAGE } from "../../../../../../../../../../_shared/_logic/workflow-progress"
  import OnboardingStepContent from "./_modules/onboarding-step-content"
  import {
    onboardingWorkflowState,
    resolveOnboardingActiveIndex,
  } from "../../_shared/_logic/onboarding-presentation"

  type OverlayHostProps = {
    onremoveOverlay?: () => void
    onshowOverlay?: () => void
    setActionButtonSnippet?: (snippet: Snippet | null) => void
  }

  type Props = {
    organizationId: string
    organizationSlug?: string | null
    organizationName?: string | null
    currentUserId?: string | null
    canUseOperationalPlanning?: boolean
    initialOnboarding: OrganizationOnboardingResponse
    onUpdated?: (response: OrganizationOnboardingResponse) => void | Promise<void>
  }

  let {
    organizationId,
    organizationSlug = null,
    organizationName = null,
    currentUserId = null,
    canUseOperationalPlanning = false,
    initialOnboarding,
    onUpdated = async () => {},
    onshowOverlay = () => {},
    setActionButtonSnippet = null,
  }: Props & OverlayHostProps = $props()

  let onboarding = $state<OrganizationOnboardingResponse>(untrack(() => initialOnboarding))
  let actionLoading = $state<string | null>(null)
  let selectedIndex = $state(resolveOnboardingActiveIndex(untrack(() => initialOnboarding)))

  const organizationSegment = $derived(organizationSlug ?? organizationId)
  const visibleSteps = $derived(onboarding.steps)
  const activeIndex = $derived(resolveOnboardingActiveIndex(onboarding))

  const applyResponse = async (response: OrganizationOnboardingResponse) => {
    onboarding = response
    selectedIndex = resolveOnboardingActiveIndex(response)
    await onUpdated?.(response)
  }

  onMount(() => {
    onshowOverlay?.()
    setActionButtonSnippet?.(null)
  })

  onDestroy(() => {
    setActionButtonSnippet?.(null)
  })

  const primaryActionIcon = (step: OrganizationOnboardingStep) => {
    if (step.primary_action === "customer_import") return mdiUpload

    if (step.primary_action === "manage_employees") return mdiAccountMultiplePlus

    if (step.primary_action === "connect_calendar") return mdiCalendarSync

    if (step.primary_action === "organization_settings") return mdiCogOutline

    if (step.primary_action === "planning_settings") return mdiCalendarClockOutline

    return mdiFolderUpload
  }

  const updateStep = async (step: OrganizationOnboardingStep, status: "COMPLETED" | "SKIPPED") => {
    if (!organizationId || actionLoading) return

    actionLoading = `${step.key}:${status}`
    try {
      const response = await updateOrganizationOnboardingStep(organizationId, step.key, status)
      await applyResponse(response)
      newNotification({
        class: "success",
        html: status === "COMPLETED" ? "Schritt gespeichert." : "Schritt übersprungen.",
      })
    } catch (err) {
      console.error("organization_onboarding_update_failed", err)
      newNotification({
        class: "error",
        html: "Onboarding-Schritt konnte nicht gespeichert werden.",
      })
    } finally {
      actionLoading = null
    }
  }

  const skipStep = async (step: OrganizationOnboardingStep) => {
    if (step.status !== "OPEN" || !step.can_skip || actionLoading) return

    const approved = await newApproval({
      title: "Schritt überspringen?",
      description:
        "Dieser Schritt wird für diese Organisation ausgeblendet. Die Einrichtung können Sie jederzeit später auf der jeweiligen Seite nachholen.",
      approvalLabel: "Ja, überspringen",
      declineLabel: "Abbrechen",
    })

    if (!approved) return

    await updateStep(step, "SKIPPED")
  }

  const navigateToCustomerRegistry = () => {
    spaNavigate(`/organizations/${encodeURIComponent(organizationSegment)}/administration/district`)
  }

  const navigateToEmployees = () => {
    spaNavigate(`/organizations/${encodeURIComponent(organizationSegment)}/administration/users`)
  }

  const navigateToCalendar = () => {
    if (!currentUserId) return

    spaNavigate(
      `/organizations/${encodeURIComponent(organizationSegment)}/users/${encodeURIComponent(
        currentUserId,
      )}/integrations`,
    )
  }

  const navigateToBatchPlanning = () => {
    spaNavigate(
      `/organizations/${encodeURIComponent(organizationSegment)}/work-orders?action=batch-upload`,
    )
  }

  const navigateToOrganizationSettings = () => {
    spaNavigate(
      `/organizations/${encodeURIComponent(organizationSegment)}/administration/organization/general`,
    )
  }

  const navigateToPlanningSettings = () => {
    spaNavigate(`/organizations/${encodeURIComponent(organizationSegment)}/administration/planning`)
  }

  const runPrimaryAction = (step: OrganizationOnboardingStep) => {
    if (step.primary_action === "customer_import") {
      navigateToCustomerRegistry()

      return
    }

    if (step.primary_action === "manage_employees") {
      navigateToEmployees()

      return
    }

    if (step.primary_action === "connect_calendar") {
      navigateToCalendar()

      return
    }

    if (step.primary_action === "organization_settings") {
      navigateToOrganizationSettings()

      return
    }

    if (step.primary_action === "planning_settings") {
      navigateToPlanningSettings()

      return
    }

    if (!canUseOperationalPlanning) return

    navigateToBatchPlanning()
  }

  const primaryActionDisabled = (step: OrganizationOnboardingStep) =>
    !canUseOperationalPlanning &&
    ![
      "customer_import",
      "manage_employees",
      "connect_calendar",
      "organization_settings",
      "planning_settings",
    ].includes(step.primary_action)

  const progressSteps = $derived<ProgressStep[]>(
    visibleSteps.map((step, index) => ({
      id: step.key,
      label: step.title,
      description: step.description,
      hideDefaultAction: true,
      workflowState: onboardingWorkflowState(onboarding, step, index),
      snippet: onboardingStep,
    })),
  )

  const handleSelectedIndexChange = (index: number) => {
    selectedIndex = index
  }
</script>

{#snippet onboardingStep(ctx: ProgressStepContext)}
  {@const step = visibleSteps[ctx.stepIndex]}
  {#if step}
    <OnboardingStepContent
      {step}
      isActive={ctx.stepIndex === activeIndex}
      isLocked={ctx.readOnly && step.status === "OPEN"}
      actionLoading={Boolean(actionLoading)}
      primaryDisabled={primaryActionDisabled(step)}
      primaryIcon={primaryActionIcon(step)}
      onPrimaryAction={() => runPrimaryAction(step)}
      onSkip={() => skipStep(step)}
    />
  {/if}
{/snippet}

<div class="organization-onboarding-overlay form light">
  <ProgressSteps
    steps={progressSteps}
    showControls={false}
    darkMode={true}
    syncUrl={false}
    statusMode="explicit"
    navigationMode="guarded"
    lockedStepMessage={LOCKED_WORKFLOW_STEP_MESSAGE}
    {selectedIndex}
    onSelectedIndexChange={handleSelectedIndexChange}
  />
</div>

<style lang="less">
  .organization-onboarding-overlay {
    display: flex;
    flex-direction: column;
    gap: 1rem;
    height: fit-content;
    padding-bottom: 1rem;
  }
</style>
